import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CalendarCheck,
  Archive,
  ArrowRight,
  ExternalLink,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  RotateCcw,
  FileSpreadsheet,
  FileText,
  Printer
} from 'lucide-react';

import { reportesAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import {
  guardarBlob,
  imprimirBlobPdf,
  limpiarParametros,
  nombreArchivoPeriodo,
  obtenerArchivoAutenticado
} from '../../utils/descargaAutenticada';
import '../../styles/CobranzaDiariaReporte.css';

const TRAMOS_CONFIG = {
  'VENCE_HOY': { label: 'Vence Hoy (0d)', className: 'tramo-vence-hoy' },
  '1-7': { label: '1–7 días', className: 'tramo-1-7' },
  '8-30': { label: '8–30 días', className: 'tramo-8-30' },
  '31-60': { label: '31–60 días', className: 'tramo-31-60' },
  '61-90': { label: '61–90 días', className: 'tramo-61-90' },
  '91+': { label: '91+ días', className: 'tramo-91-plus' }
};

const PRESETS = [
  { id: 'HOY', label: 'Hoy' },
  { id: 'MANANA', label: 'Mañana' },
  { id: 'DIA_ESPECIFICO', label: 'Día Específico' },
  { id: 'ESTA_SEMANA_LV', label: 'Esta Semana (L–V)' },
  { id: 'PROXIMA_SEMANA_LV', label: 'Próxima Semana (L–V)' },
  { id: 'SEMANA_COMPLETA', label: 'Semana Completa (L–D)' },
  { id: 'RANGO_PERSONALIZADO', label: 'Rango Personalizado' }
];

const DIAS_SEMANA_OPCIONES = [
  { id: 1, label: 'L', title: 'Lunes' },
  { id: 2, label: 'M', title: 'Martes' },
  { id: 3, label: 'M', title: 'Miércoles' },
  { id: 4, label: 'J', title: 'Jueves' },
  { id: 5, label: 'V', title: 'Viernes' },
  { id: 6, label: 'S', title: 'Sábado' },
  { id: 0, label: 'D', title: 'Domingo' }
];

const PREVIEW_LIMIT = 15;
const DEFAULT_PAGE_SIZE = 25;

export default function CobranzaDiaria() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Universo: 'AGENDA' | 'BACKLOG'
  const [universo, setUniverso] = useState('AGENDA');

  // Periodo & Fechas
  const [periodoPreset, setPeriodoPreset] = useState('HOY');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [diasSemana, setDiasSemana] = useState([1, 2, 3, 4, 5, 6, 0]);

  // Modos de visualización
  const [modoAgenda, setModoAgenda] = useState('PREVIEW');
  const [modoBacklog, setModoBacklog] = useState('RESUMEN');

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filtros
  const [search, setSearch] = useState('');
  const [asesor, setAsesor] = useState('TODOS');
  const [tipo, setTipo] = useState('TODOS');
  const [frecuencia, setFrecuencia] = useState('TODAS');
  const [agrupacionId, setAgrupacionId] = useState('TODAS');
  const [tramo, setTramo] = useState('TODOS');
  const [estado, setEstado] = useState('TODOS');

  // Detalle expandible
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Dropdown de exportación
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cargarReporte = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        periodoPreset,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        diasSemana: diasSemana.join(','),
        universo,
        asesor: asesor !== 'TODOS' ? asesor : undefined,
        tipo: tipo !== 'TODOS' ? tipo : undefined,
        frecuencia: frecuencia !== 'TODAS' ? frecuencia : undefined,
        agrupacionId: agrupacionId !== 'TODAS' ? agrupacionId : undefined,
        tramo: tramo !== 'TODOS' ? tramo : undefined,
        estado: estado !== 'TODOS' ? estado : undefined,
        search: search.trim() || undefined
      };

      const res = await reportesAPI.cobranza(params);
      setData(res.data);

      if (res.data?.corte?.fechaDesde && !fechaDesde) {
        setFechaDesde(res.data.corte.fechaDesde);
      }
      if (res.data?.corte?.fechaHasta && !fechaHasta) {
        setFechaHasta(res.data.corte.fechaHasta);
      }
    } catch (err) {
      const errInfo = describirError(err);
      setError(errInfo.mensaje || 'Error al cargar el reporte de cobranza.');
    } finally {
      setLoading(false);
    }
  }, [periodoPreset, fechaDesde, fechaHasta, diasSemana, universo, asesor, tipo, frecuencia, agrupacionId, tramo, estado, search]);

  useEffect(() => {
    cargarReporte();
  }, [cargarReporte]);

  // Cambiar preset de periodo
  const handleCambiarPreset = (presetId) => {
    setPeriodoPreset(presetId);
    setPaginaActual(1);
    if (presetId === 'ESTA_SEMANA_LV' || presetId === 'PROXIMA_SEMANA_LV') {
      setDiasSemana([1, 2, 3, 4, 5]);
    } else {
      setDiasSemana([1, 2, 3, 4, 5, 6, 0]);
    }
  };

  const toggleDiaSemana = (dayId) => {
    setDiasSemana((prev) => {
      let next;
      if (prev.includes(dayId)) {
        if (prev.length === 1) return prev; // Mantener al menos un día
        next = prev.filter((d) => d !== dayId);
      } else {
        next = [...prev, dayId];
      }
      return next;
    });
  };

  // Cambiar universo
  const handleCambiarUniverso = (nuevoUniverso) => {
    setUniverso(nuevoUniverso);
    setTramo('TODOS');
    setPaginaActual(1);
    if (nuevoUniverso === 'AGENDA') {
      setModoAgenda('PREVIEW');
    } else {
      setModoBacklog('RESUMEN');
    }
  };

  const handleLimpiarFiltros = () => {
    setSearch('');
    setAsesor('TODOS');
    setTipo('TODOS');
    setFrecuencia('TODAS');
    setAgrupacionId('TODAS');
    setTramo('TODOS');
    setEstado('TODOS');
    setPaginaActual(1);
  };

  const toggleRowExpand = (creditoId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(creditoId)) next.delete(creditoId);
      else next.add(creditoId);
      return next;
    });
  };

  const formatearFechaDisplay = (fechaIso) => {
    if (!fechaIso) return '—';
    const partes = fechaIso.split('-');
    if (partes.length === 3) {
      return `${partes[2]}/${partes[1]}/${partes[0]}`;
    }
    return fechaIso;
  };

  // Helpers de Exportación
  const buildExportParams = () => {
    return {
      periodoPreset,
      fechaDesde: fechaDesde || undefined,
      fechaHasta: fechaHasta || undefined,
      diasSemana: diasSemana.join(','),
      universo,
      asesor: asesor !== 'TODOS' ? asesor : undefined,
      tipo: tipo !== 'TODOS' ? tipo : undefined,
      frecuencia: frecuencia !== 'TODAS' ? frecuencia : undefined,
      agrupacionId: agrupacionId !== 'TODAS' ? agrupacionId : undefined,
      tramo: tramo !== 'TODOS' ? tramo : undefined,
      estado: estado !== 'TODOS' ? estado : undefined,
      search: search.trim() || undefined
    };
  };

  // Descargas AUTENTICADAS (utils/descargaAutenticada): apiClient con Bearer y
  // responseType 'blob'; el token nunca viaja en la URL. Excel, PDF e
  // impresión reutilizan buildExportParams(), los mismos filtros de pantalla.
  const [descargando, setDescargando] = useState(null); // null | 'excel' | 'pdf' | 'imprimir'

  const obtenerArchivoCobranza = (tipoArchivo) => {
    const params = limpiarParametros(buildExportParams());
    return obtenerArchivoAutenticado(tipoArchivo, () => reportesAPI.descargarCobranzaArchivo(tipoArchivo, params));
  };

  // El nombre lleva el periodo que el backend resolvió para la consulta en
  // pantalla (data.corte), no la fecha de generación.
  const nombreArchivoCobranza = (tipoArchivo) => nombreArchivoPeriodo(
    universo === 'BACKLOG' ? 'Cartera_Vencida' : 'Cobranza_Diaria',
    tipoArchivo,
    data?.corte?.fechaDesde,
    data?.corte?.fechaHasta
  );

  // Mientras el reporte recarga (cambio de periodo/filtros), data.corte aún es
  // el del periodo anterior: exportar entonces mezclaría nombre y contenido.
  const descargarCobranza = async (tipoArchivo) => {
    setExportOpen(false);
    if (descargando || loading) return;
    setDescargando(tipoArchivo);
    try {
      const nombre = nombreArchivoCobranza(tipoArchivo);
      guardarBlob(await obtenerArchivoCobranza(tipoArchivo), nombre);
    } catch (err) {
      alert(err.message || 'No se pudo descargar el archivo.');
    } finally {
      setDescargando(null);
    }
  };

  const handleExportPdf = () => descargarCobranza('pdf');
  const handleExportExcel = () => descargarCobranza('excel');

  // Impresión formal: imprime el MISMO PDF autenticado del reporte, no la pantalla.
  const handlePrintFormal = async () => {
    setExportOpen(false);
    if (descargando || loading) return;
    setDescargando('imprimir');
    try {
      nombreArchivoCobranza('pdf'); // exige periodo resuelto
      imprimirBlobPdf(await obtenerArchivoCobranza('pdf'));
    } catch (err) {
      alert(err.message || 'No se pudo preparar la impresión.');
    } finally {
      setDescargando(null);
    }
  };

  const corte = data?.corte || {};
  const resumenBacklog = data?.resumenBacklog || {};
  const totalesGlobales = data?.totalesGlobales || {};
  const catalogos = data?.catalogos || { asesores: [], agrupaciones: [] };
  const registros = useMemo(() => data?.registros || [], [data?.registros]);

  const esRango = corte.fechaDesde && corte.fechaHasta && corte.fechaDesde !== corte.fechaHasta;

  // Título Dinámico
  let tituloDinamico = 'LISTADO DE COBRANZA';
  if (universo === 'BACKLOG') {
    tituloDinamico = 'REPORTE DE CARTERA VENCIDA / RECUPERACIÓN';
  } else if (!esRango) {
    tituloDinamico = `LISTADO DE COBRANZA DEL DÍA`;
  } else if (periodoPreset === 'ESTA_SEMANA_LV' || periodoPreset === 'PROXIMA_SEMANA_LV') {
    tituloDinamico = `LISTADO SEMANAL DE COBRANZA`;
  } else {
    tituloDinamico = `LISTADO DE COBRANZA`;
  }

  // Cálculos de paginación y vista previa
  const totalRegistrosFiltrados = registros.length;

  const registrosPaginados = useMemo(() => {
    if (universo === 'AGENDA' && modoAgenda === 'PREVIEW') {
      return registros.slice(0, PREVIEW_LIMIT);
    }
    if (pageSize === 'TODOS') {
      return registros;
    }
    const start = (paginaActual - 1) * Number(pageSize);
    return registros.slice(start, start + Number(pageSize));
  }, [registros, universo, modoAgenda, paginaActual, pageSize]);

  const totalPaginas = pageSize === 'TODOS' ? 1 : Math.ceil(totalRegistrosFiltrados / Number(pageSize)) || 1;
  const tieneFiltrosActivos = search.trim() !== '' || asesor !== 'TODOS' || tipo !== 'TODOS' || frecuencia !== 'TODAS' || agrupacionId !== 'TODAS' || tramo !== 'TODOS' || estado !== 'TODOS';

  return (
    <div className="reporte-cobranza-container">
      {/* Header del Reporte */}
      <header className="reporte-header">
        <div className="reporte-title-section">
          <div className="reporte-title-row">
            <h1 className="reporte-title">{tituloDinamico}</h1>
            {corte.esHistorica ? (
              <span className="badge-historico">
                <AlertCircle size={14} />
                CONSULTA HISTÓRICA
              </span>
            ) : (
              <span className="badge-operativo">OPERACIÓN DEL DÍA</span>
            )}
          </div>
          <div className="reporte-meta">
            <span>
              Periodo: <strong>{corte.periodoLabel || `${formatearFechaDisplay(corte.fechaDesde)} – ${formatearFechaDisplay(corte.fechaHasta)}`}</strong>
            </span>
            <span>
              Generado: <strong>{corte.generado || '—'}</strong>
            </span>
            <span>
              Zona Horaria: <strong>{corte.timezone || 'America/Mexico_City'}</strong>
            </span>
          </div>
        </div>

        <div className="reporte-actions">
          <button
            className="btn-reporte-action"
            onClick={() => cargarReporte()}
            disabled={loading}
            title="Refrescar datos"
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Actualizar
          </button>

          {/* Menú Dropdown Exportar Reporte */}
          <div className="export-menu-container" ref={exportRef}>
            <button
              type="button"
              className="btn-reporte-action primary"
              onClick={() => setExportOpen((prev) => !prev)}
              disabled={!!descargando || loading}
              aria-busy={!!descargando}
              title={loading ? 'Espera a que termine de cargar el reporte' : 'Exportar reporte formal'}
            >
              {descargando ? <RefreshCw size={14} className="spin" /> : <FileSpreadsheet size={14} />}
              {descargando ? 'GENERANDO…' : 'EXPORTAR REPORTE'}
              <ChevronDown size={14} />
            </button>

            {exportOpen && (
              <div className="export-dropdown-panel">
                <button type="button" className="export-option-item" onClick={handleExportPdf}>
                  <FileText size={16} color="#dc2626" />
                  <div>
                    <strong>Descargar PDF</strong>
                    <span className="export-option-desc">Documento formal landscape</span>
                  </div>
                </button>

                <button type="button" className="export-option-item" onClick={handleExportExcel}>
                  <FileSpreadsheet size={16} color="#16a34a" />
                  <div>
                    <strong>Descargar Excel (.xlsx)</strong>
                    <span className="export-option-desc">Hojas Resumen + Detalle completo</span>
                  </div>
                </button>

                <button type="button" className="export-option-item" onClick={handlePrintFormal}>
                  <Printer size={16} color="#0284c7" />
                  <div>
                    <strong>Impresión Formal</strong>
                    <span className="export-option-desc">Vista documental sin interfaz</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner mb-3" style={{ padding: '0.85rem 1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '0.875rem' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Selector de Periodo de Cobranza (Fase 1B) */}
      <section className="periodo-selector-card">
        <div className="periodo-header-row">
          <div className="periodo-label">
            <Calendar size={15} color="#0284c7" />
            Periodo de Cobranza
          </div>
          <div className="periodo-presets-group">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`periodo-preset-pill ${periodoPreset === p.id ? 'active' : ''}`}
                onClick={() => handleCambiarPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Opciones de fechas personalizadas y selector de días de la semana */}
        {(periodoPreset === 'DIA_ESPECIFICO' || periodoPreset === 'RANGO_PERSONALIZADO' || esRango) && (
          <div className="periodo-custom-dates">
            {periodoPreset === 'DIA_ESPECIFICO' ? (
              <div className="date-range-input-box">
                <span>Día:</span>
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setFechaHasta(e.target.value);
                  }}
                />
              </div>
            ) : (
              <>
                <div className="date-range-input-box">
                  <span>Desde:</span>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                  />
                </div>
                <div className="date-range-input-box">
                  <span>Hasta:</span>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                  />
                </div>
              </>
            )}

            {esRango && (
              <div className="weekday-chips-container">
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginRight: '4px' }}>
                  Días incluidos:
                </span>
                {DIAS_SEMANA_OPCIONES.map((dia) => (
                  <button
                    key={dia.id}
                    type="button"
                    title={dia.title}
                    className={`weekday-chip ${diasSemana.includes(dia.id) ? 'active' : ''}`}
                    onClick={() => toggleDiaSemana(dia.id)}
                  >
                    {dia.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Selector de Universo Principal (Pestañas) */}
      <div className="universo-tabs-wrapper">
        <button
          type="button"
          className={`universo-tab-btn ${universo === 'AGENDA' ? 'active-agenda' : ''}`}
          onClick={() => handleCambiarUniverso('AGENDA')}
        >
          <CalendarCheck size={18} />
          AGENDA DE COBRANZA DEL PERIODO ({totalesGlobales.agendaCreditos || 0})
        </button>

        <button
          type="button"
          className={`universo-tab-btn ${universo === 'BACKLOG' ? 'active-backlog' : ''}`}
          onClick={() => handleCambiarUniverso('BACKLOG')}
        >
          <Archive size={18} />
          BACKLOG VENCIDO / RECUPERACIÓN ({totalesGlobales.backlogCreditos || 0})
        </button>
      </div>

      {universo === 'AGENDA' ? (
        <>
          {/* Hero Protagonista: COBRANZA PROGRAMADA (0 DÍAS) */}
          <section className="hero-cobranza-programada-card">
        <div className="hero-programada-header">
          <span className="hero-badge">PRIORIDAD 1 · 0 DÍAS</span>
          <h2 className="hero-programada-title">
            {esRango ? 'COBRANZA PROGRAMADA DEL PERIODO' : 'COBRANZA PROGRAMADA HOY'}
          </h2>
          <p className="hero-programada-desc">
            {esRango 
              ? 'Obligaciones contractuales con vencimiento en las fechas seleccionadas del periodo.' 
              : 'Obligaciones contractuales cuyo vencimiento cae estrictamente en la fecha consultada (sin mora previa mezclada).'}
          </p>
        </div>
        <div className="hero-programada-metrics-grid">
          <div className="hero-metric-item main">
            <span className="hero-metric-label">Importe Programado</span>
            <span className="hero-metric-value">{formatoMoneda(data?.resumenCanonica?.cobranzaProgramada?.montoProgramado || 0)}</span>
            <span className="hero-metric-sub">
              {data?.resumenCanonica?.cobranzaProgramada?.alCorriente?.creditos || 0} al cte ({formatoMoneda(data?.resumenCanonica?.cobranzaProgramada?.alCorriente?.monto || 0)}) · {data?.resumenCanonica?.cobranzaProgramada?.conAtrasoPrevio?.creditos || 0} con mora ({formatoMoneda(data?.resumenCanonica?.cobranzaProgramada?.conAtrasoPrevio?.monto || 0)})
            </span>
          </div>
          <div className="hero-metric-item">
            <span className="hero-metric-label">Créditos con Cuota</span>
            <span className="hero-metric-value">{data?.resumenCanonica?.cobranzaProgramada?.creditos || 0}</span>
            <span className="hero-metric-sub">
              {data?.resumenCanonica?.cobranzaProgramada?.alCorriente?.creditos || 0} al corriente · {data?.resumenCanonica?.cobranzaProgramada?.conAtrasoPrevio?.creditos || 0} con mora previa
            </span>
          </div>
          <div className="hero-metric-item">
            <span className="hero-metric-label">Obligaciones</span>
            <span className="hero-metric-value">{data?.resumenCanonica?.cobranzaProgramada?.obligaciones || data?.resumenCanonica?.cobranzaProgramada?.creditos || 0}</span>
            <span className="hero-metric-sub">cuotas exigibles hoy</span>
          </div>
          <div className="hero-metric-item">
            <span className="hero-metric-label">Clientes Únicos</span>
            <span className="hero-metric-value">{data?.resumenCanonica?.cobranzaProgramada?.clientesUnicos || 0}</span>
            <span className="hero-metric-sub">titulares</span>
          </div>
          <div className="hero-metric-item collected">
            <span className="hero-metric-label">Cobrado</span>
            <span className="hero-metric-value">{formatoMoneda(data?.resumenCanonica?.cobranzaProgramada?.cobrado || 0)}</span>
            <span className="hero-metric-sub">aplicado a cuotas de hoy</span>
          </div>
          <div className="hero-metric-item pending">
            <span className="hero-metric-label">Pendiente Programado</span>
            <span className="hero-metric-value">{formatoMoneda(data?.resumenCanonica?.cobranzaProgramada?.pendienteProgramado || 0)}</span>
            <span className="hero-metric-sub">por recaudar hoy</span>
          </div>
        </div>
      </section>

      {/* Bloque Secundario: RECUPERACIÓN VENCIDA (Orden obligatorio: más reciente a más antigua) */}
      <section className="recuperacion-vencida-section">
        <div className="recuperacion-header-row">
          <div>
            <h3 className="section-sub-title">RECUPERACIÓN VENCIDA</h3>
            <span className="section-sub-desc">
              {esRango 
                ? 'Recuperación pendiente al inicio del periodo (contabilizada una sola vez por tramo).' 
                : 'Obligaciones vencidas antes del corte que continúan pendientes de regularización.'}
            </span>
          </div>
          {tramo !== 'TODOS' && (
            <button
              type="button"
              className="btn-reporte-action"
              style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
              onClick={() => setTramo('TODOS')}
            >
              Mostrar Todos los Tramos
            </button>
          )}
        </div>

        <div className="recuperacion-tramos-grid">
          {/* Prioridad 2: 1–7 días */}
          <div
            className={`tramo-operativo-card ${tramo === '1-7' ? 'active' : ''}`}
            onClick={() => {
              setTramo(tramo === '1-7' ? 'TODOS' : '1-7');
              setPaginaActual(1);
            }}
          >
            <div className="tramo-header">
              <span className="tramo-badge p2">PRIORIDAD 2 · 1–7 DÍAS</span>
              <span className="tramo-creditos">{data?.resumenCanonica?.recuperacionVencida?.['1-7']?.creditos || 0} créd. ({data?.resumenCanonica?.recuperacionVencida?.['1-7']?.clientes || 0} cli.)</span>
            </div>
            <div className="tramo-monto">{formatoMoneda(data?.resumenCanonica?.recuperacionVencida?.['1-7']?.monto || 0)}</div>
            <div className="tramo-footer">Mora temprana · Acción inmediata</div>
          </div>

          {/* Prioridad 3: 8–30 días */}
          <div
            className={`tramo-operativo-card ${tramo === '8-30' ? 'active' : ''}`}
            onClick={() => {
              setTramo(tramo === '8-30' ? 'TODOS' : '8-30');
              setPaginaActual(1);
            }}
          >
            <div className="tramo-header">
              <span className="tramo-badge p3">PRIORIDAD 3 · 8–30 DÍAS</span>
              <span className="tramo-creditos">{data?.resumenCanonica?.recuperacionVencida?.['8-30']?.creditos || 0} créd. ({data?.resumenCanonica?.recuperacionVencida?.['8-30']?.clientes || 0} cli.)</span>
            </div>
            <div className="tramo-monto">{formatoMoneda(data?.resumenCanonica?.recuperacionVencida?.['8-30']?.monto || 0)}</div>
            <div className="tramo-footer">Mora preventiva · Seguimiento</div>
          </div>

          {/* Prioridad 4: 31–60 días */}
          <div
            className={`tramo-operativo-card ${tramo === '31-60' ? 'active' : ''}`}
            onClick={() => {
              setTramo(tramo === '31-60' ? 'TODOS' : '31-60');
              setPaginaActual(1);
            }}
          >
            <div className="tramo-header">
              <span className="tramo-badge p4">PRIORIDAD 4 · 31–60 DÍAS</span>
              <span className="tramo-creditos">{data?.resumenCanonica?.recuperacionVencida?.['31-60']?.creditos || 0} créd. ({data?.resumenCanonica?.recuperacionVencida?.['31-60']?.clientes || 0} cli.)</span>
            </div>
            <div className="tramo-monto">{formatoMoneda(data?.resumenCanonica?.recuperacionVencida?.['31-60']?.monto || 0)}</div>
            <div className="tramo-footer">Riesgo medio · Recuperación</div>
          </div>

          {/* Prioridad 5: 61–90 días */}
          <div
            className={`tramo-operativo-card ${tramo === '61-90' ? 'active' : ''}`}
            onClick={() => {
              setTramo(tramo === '61-90' ? 'TODOS' : '61-90');
              setPaginaActual(1);
            }}
          >
            <div className="tramo-header">
              <span className="tramo-badge p5">PRIORIDAD 5 · 61–90 DÍAS</span>
              <span className="tramo-creditos">{data?.resumenCanonica?.recuperacionVencida?.['61-90']?.creditos || 0} créd. ({data?.resumenCanonica?.recuperacionVencida?.['61-90']?.clientes || 0} cli.)</span>
            </div>
            <div className="tramo-monto">{formatoMoneda(data?.resumenCanonica?.recuperacionVencida?.['61-90']?.monto || 0)}</div>
            <div className="tramo-footer">Riesgo alto · Gestión especial</div>
          </div>

          {/* Prioridad 6: 91+ días (Colapsado por defecto) */}
          <div
            className={`tramo-operativo-card deep ${tramo === '91+' ? 'active' : ''}`}
            onClick={() => {
              setTramo(tramo === '91+' ? 'TODOS' : '91+');
              setPaginaActual(1);
            }}
          >
            <div className="tramo-header">
              <span className="tramo-badge p6">PRIORIDAD 6 · 91+ DÍAS</span>
              <span className="tramo-creditos">{data?.resumenCanonica?.recuperacionVencida?.['91+']?.creditos || 0} créd. ({data?.resumenCanonica?.recuperacionVencida?.['91+']?.clientes || 0} cli.)</span>
            </div>
            <div className="tramo-monto">{formatoMoneda(data?.resumenCanonica?.recuperacionVencida?.['91+']?.monto || 0)}</div>
            <div className="tramo-footer">Mora profunda · Cartera de castigo</div>
          </div>
        </div>
      </section>

      {/* Bloque Terciario: PROMESAS DE PAGO y COBRANZA REALIZADA (Independientes) */}
      <section className="operacion-adicional-row">
        <div className="operacion-stat-card">
          <div className="operacion-stat-title">PROMESAS DEL DÍA / PERIODO</div>
          <div className="operacion-stat-body">
            <span className="operacion-stat-num">{data?.resumenCanonica?.promesas?.totalPromesas || 0} compromisos</span>
            <span className="operacion-stat-amount">{formatoMoneda(data?.resumenCanonica?.promesas?.importePrometido || 0)}</span>
          </div>
          <span className="operacion-stat-note">Registros informativos independientes (no sumados a cobranza programada ni cobrado).</span>
        </div>

        <div className="operacion-stat-card">
          <div className="operacion-stat-title">COBRANZA REALIZADA (PAGOS RECIBIDOS)</div>
          <div className="operacion-stat-body">
            <span className="operacion-stat-num">{data?.resumenCanonica?.cobranzaRealizada?.totalPagos || 0} pagos aplicados</span>
            <span className="operacion-stat-amount" style={{ color: '#0284c7' }}>{formatoMoneda(data?.resumenCanonica?.cobranzaRealizada?.importeCobrado || 0)}</span>
          </div>
          <span className="operacion-stat-note">Recaudación efectivamente procesada en el corte consultado.</span>
        </div>
      </section>
        </>
      ) : (
        <>
          {/* Tarjetas de Tramos de Backlog (Modo Resumen Inicial) */}
          {modoBacklog === 'RESUMEN' && (
            <div className="backlog-tramos-grid">
              <div className="backlog-tramo-card low">
                <div className="backlog-tramo-title">
                  <span>TRAMO 31–60 DÍAS</span>
                  <span className="badge-tramo tramo-31-60">Riesgo</span>
                </div>
                <div className="backlog-tramo-stats">
                  <div className="backlog-tramo-credits">{resumenBacklog.tramos?.['31-60']?.creditos || 0} créditos</div>
                  <div className="backlog-tramo-amount">Saldo: {formatoMoneda(resumenBacklog.tramos?.['31-60']?.saldoTotal || 0)}</div>
                </div>
                <button
                  type="button"
                  className="btn-tramo-action"
                  onClick={() => {
                    setTramo('31-60');
                    setModoBacklog('FULL');
                  }}
                >
                  Ver {resumenBacklog.tramos?.['31-60']?.creditos || 0} Créditos
                </button>
              </div>

              <div className="backlog-tramo-card medium">
                <div className="backlog-tramo-title">
                  <span>TRAMO 61–90 DÍAS</span>
                  <span className="badge-tramo tramo-61-90">Riesgo Alto</span>
                </div>
                <div className="backlog-tramo-stats">
                  <div className="backlog-tramo-credits">{resumenBacklog.tramos?.['61-90']?.creditos || 0} créditos</div>
                  <div className="backlog-tramo-amount">Saldo: {formatoMoneda(resumenBacklog.tramos?.['61-90']?.saldoTotal || 0)}</div>
                </div>
                <button
                  type="button"
                  className="btn-tramo-action"
                  onClick={() => {
                    setTramo('61-90');
                    setModoBacklog('FULL');
                  }}
                >
                  Ver {resumenBacklog.tramos?.['61-90']?.creditos || 0} Créditos
                </button>
              </div>

              <div className="backlog-tramo-card critical">
                <div className="backlog-tramo-title">
                  <span>TRAMO 91+ DÍAS</span>
                  <span className="badge-tramo tramo-91-plus">Recuperación</span>
                </div>
                <div className="backlog-tramo-stats">
                  <div className="backlog-tramo-credits">{resumenBacklog.tramos?.['91+']?.creditos || 0} créditos</div>
                  <div className="backlog-tramo-amount">Saldo: {formatoMoneda(resumenBacklog.tramos?.['91+']?.saldoTotal || 0)}</div>
                </div>
                <button
                  type="button"
                  className="btn-tramo-action"
                  onClick={() => {
                    setTramo('91+');
                    setModoBacklog('FULL');
                  }}
                >
                  Ver {resumenBacklog.tramos?.['91+']?.creditos || 0} Créditos
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Si Backlog está en modo resumen, dar acceso a ver todo */}
      {universo === 'BACKLOG' && modoBacklog === 'RESUMEN' ? (
        <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
          <button
            type="button"
            className="btn-reporte-action primary"
            style={{ padding: '0.75rem 1.75rem', fontSize: '0.9375rem' }}
            onClick={() => setModoBacklog('FULL')}
          >
            <Archive size={16} />
            VER BACKLOG COMPLETO ({totalesGlobales.backlogCreditos || 0} CRÉDITOS)
          </button>
        </div>
      ) : (
        /* Modo Tabla (Agenda o Backlog desplegado) */
        <>
          {/* Barra de Filtros Compacta */}
          <section className="reporte-filter-bar">
            <div className="filter-grid-compact">
              <div className="search-input-wrapper">
                <Search size={15} className="search-icon-pos" />
                <input
                  type="text"
                  placeholder="Buscar cliente, folio o teléfono..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPaginaActual(1);
                  }}
                />
              </div>

              <select
                className="filter-select"
                value={asesor}
                onChange={(e) => {
                  setAsesor(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODOS">Todos los Asesores</option>
                {catalogos.asesores?.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>

              <select
                className="filter-select"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODOS">Todas las Modalidades</option>
                <option value="PERSONAL">Personal</option>
                <option value="GRUPAL">Grupal</option>
                <option value="COLOCADORA">Colocadora</option>
              </select>
            </div>

            <div className="filter-grid-compact-row2">
              <select
                className="filter-select"
                value={frecuencia}
                onChange={(e) => {
                  setFrecuencia(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODAS">Todas las Frecuencias</option>
                <option value="SEMANAL">Semanal</option>
                <option value="QUINCENAL">Quincenal</option>
              </select>

              <select
                className="filter-select"
                value={agrupacionId}
                onChange={(e) => {
                  setAgrupacionId(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODAS">Todas las Agrupaciones</option>
                {catalogos.agrupaciones?.map((ag) => (
                  <option key={ag.id} value={ag.id}>{ag.nombre} ({ag.tipo})</option>
                ))}
              </select>

              <select
                className="filter-select"
                value={tramo}
                onChange={(e) => {
                  setTramo(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODOS">Todos los Tramos</option>
                {universo === 'AGENDA' ? (
                  <>
                    <option value="VENCE_HOY">Vence Hoy (0 días)</option>
                    <option value="1-7">1–7 días</option>
                    <option value="8-30">8–30 días</option>
                  </>
                ) : (
                  <>
                    <option value="31-60">31–60 días</option>
                    <option value="61-90">61–90 días</option>
                    <option value="91+">91+ días</option>
                  </>
                )}
              </select>

              <select
                className="filter-select"
                value={estado}
                onChange={(e) => {
                  setEstado(e.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="TODOS">Todos los Estados</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="CON_PAGO">Con Pago</option>
                <option value="REGULARIZADO">Regularizado</option>
              </select>

              {tieneFiltrosActivos && (
                <button
                  type="button"
                  className="btn-clear-filters"
                  onClick={handleLimpiarFiltros}
                  title="Restablecer todos los filtros"
                >
                  <RotateCcw size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Limpiar Filtros
                </button>
              )}
            </div>
          </section>

          {/* Banner de Estado y Preview */}
          <div className="filter-status-bar">
            <span>
              Mostrando <strong className="filter-counter-badge">{registrosPaginados.length}</strong> de <strong>{totalRegistrosFiltrados}</strong> créditos {universo === 'AGENDA' ? 'en Agenda' : 'en Backlog'}
            </span>

            {universo === 'BACKLOG' && modoBacklog === 'FULL' && (
              <button
                type="button"
                className="btn-reporte-action"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                onClick={() => setModoBacklog('RESUMEN')}
              >
                ← Volver al Resumen de Tramos
              </button>
            )}
          </div>

          {universo === 'AGENDA' && modoAgenda === 'PREVIEW' && totalRegistrosFiltrados > PREVIEW_LIMIT && (
            <div className="preview-mode-banner">
              <div className="preview-mode-text">
                <strong>PRIORIDAD DEL PERIODO</strong>
                <span>Primeros {PREVIEW_LIMIT} casos ordenados por exigibilidad y mayor monto a recuperar.</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn-reporte-action primary"
                  onClick={() => setModoAgenda('FULL')}
                >
                  Ver Agenda Completa ({totalRegistrosFiltrados})
                  <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  className="btn-reporte-action"
                  onClick={handleExportPdf}
                >
                  <FileText size={14} />
                  Descargar PDF
                </button>
              </div>
            </div>
          )}

          {/* Tabla Principal de Columnas (Cero Desbordamiento Horizontal) */}
          <section className="reporte-table-container">
            <table className="reporte-table">
              <thead>
                <tr>
                  <th style={{ width: '28px' }}></th>
                  {esRango && <th>Fecha Cobro</th>}
                  <th>Cliente</th>
                  <th>Folio</th>
                  <th>Modalidad</th>
                  <th>Frecuencia</th>
                  <th className="text-right">{esRango ? 'Programado Periodo' : 'Programado'}</th>
                  <th className="text-right">Vencido Ant.</th>
                  <th className="text-right">Moratorio</th>
                  <th className="text-right">Cobrado</th>
                  <th className="text-right">Pendiente</th>
                  <th>Atraso</th>
                  <th style={{ textAlign: 'center' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {registrosPaginados.length === 0 ? (
                  <tr>
                    <td colSpan={esRango ? 12 : 11} style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                      No se encontraron créditos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  registrosPaginados.map((reg) => {
                    const isExpanded = expandedRows.has(reg.creditoId);
                    const tramoInfo = TRAMOS_CONFIG[reg.tramo] || { label: reg.tramo, className: '' };

                    return (
                      <React.Fragment key={reg.creditoId}>
                        <tr className={isExpanded ? 'row-expanded' : ''}>
                          <td>
                            <button
                              type="button"
                              onClick={() => toggleRowExpand(reg.creditoId)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                              title={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </td>
                          {esRango && (
                            <td>
                              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#334155' }}>
                                {reg.fechaCobro ? formatearFechaDisplay(reg.fechaCobro) : (reg.fechaVencimiento ? formatearFechaDisplay(reg.fechaVencimiento) : '—')}
                              </span>
                            </td>
                          )}
                          <td>
                            <div>
                              <strong>{reg.clienteNombre}</strong>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{reg.clienteFolio}</div>
                            </div>
                          </td>
                          <td>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0284c7' }}>
                              {reg.folio}
                            </span>
                            {reg.estadoPrevioCredito === 'CON_ATRASO_PREVIO' && (
                              <div style={{ fontSize: '0.65rem', color: '#d97706', fontWeight: 700, marginTop: '2px' }}>
                                + Mora Previa
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`badge-tipo tipo-${reg.tipoCartera.toLowerCase()}`}>
                              {reg.tipoCartera}
                            </span>
                            {reg.agrupacionNombre && (
                              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {reg.agrupacionNombre}
                              </div>
                            )}
                          </td>
                          <td>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{reg.frecuencia}</span>
                          </td>
                          <td className="text-right">{formatoMoneda(reg.cuotaVenceHoy)}</td>
                          <td className="text-right" style={{ color: reg.vencidoAnterior > 0 ? '#dc2626' : '#64748b' }}>
                            {formatoMoneda(reg.vencidoAnterior)}
                          </td>
                          <td className="text-right" style={{ color: reg.moratorio > 0 ? '#b45309' : '#64748b' }}>
                            {formatoMoneda(reg.moratorio)}
                          </td>
                          <td className="text-right" style={{ color: '#0284c7', fontWeight: 600 }}>
                            {formatoMoneda(reg.cobradoHoy)}
                          </td>
                          <td className="text-right" style={{ fontWeight: 700, color: '#dc2626' }}>
                            {formatoMoneda(reg.pendienteAlCorte)}
                          </td>
                          <td>
                            <span className={`badge-tramo ${tramoInfo.className}`}>
                              {tramoInfo.label}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="btn-ficha-action"
                              onClick={() => navigate(`/creditos/${reg.creditoId}`)}
                              title="Abrir expediente del crédito"
                            >
                              <ExternalLink size={12} />
                              Ficha
                            </button>
                          </td>
                        </tr>

                        {/* Detalle Expandible */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={esRango ? 12 : 11} style={{ padding: 0 }}>
                              <div className="row-detail-container">
                                <div className="detail-block">
                                  <div className="detail-block-title">Contacto & Asignación</div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Teléfono:</span>
                                    <span className="detail-item-val">{reg.telefono}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Asesor:</span>
                                    <span className="detail-item-val" style={{ color: '#64748b' }}>{reg.asesor}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Agrupación:</span>
                                    <span className="detail-item-val">{reg.agrupacionNombre || 'Sin agrupación'}</span>
                                  </div>
                                </div>

                                <div className="detail-block">
                                  <div className="detail-block-title">Situación Financiera</div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Saldo Total:</span>
                                    <span className="detail-item-val">{formatoMoneda(reg.saldoTotal)}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Por Regularizar:</span>
                                    <span className="detail-item-val">{formatoMoneda(reg.montoRegularizar)}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Moratorio:</span>
                                    <span className="detail-item-val" style={{ color: '#64748b' }}>No disponible</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-item-label">Días Atraso:</span>
                                    <span className="detail-item-val">{reg.diasAtraso} días</span>
                                  </div>
                                </div>

                                <div className="detail-block">
                                  <div className="detail-block-title">Promesa de Pago</div>
                                  {reg.promesa ? (
                                    <>
                                      <div className="detail-item">
                                        <span className="detail-item-label">Fecha:</span>
                                        <span className="detail-item-val">{formatearFechaDisplay(reg.promesa.fechaPromesa)}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-item-label">Importe:</span>
                                        <span className="detail-item-val">{formatoMoneda(reg.promesa.importePrometido)}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-item-label">Estado:</span>
                                        <span className="detail-item-val">{reg.promesa.estado}</span>
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{ color: '#94a3b8', padding: '0.4rem 0', fontSize: '0.75rem' }}>Sin promesa registrada.</div>
                                  )}
                                </div>

                                <div className="detail-block">
                                  <div className="detail-block-title">Última Gestión Operativa</div>
                                  {reg.ultimaGestion ? (
                                    <>
                                      <div className="detail-item">
                                        <span className="detail-item-label">Fecha:</span>
                                        <span className="detail-item-val">{reg.ultimaGestion.fechaGestion}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-item-label">Resultado:</span>
                                        <span className="detail-item-val">{reg.ultimaGestion.resultado || reg.ultimaGestion.tipo || '—'}</span>
                                      </div>
                                      {reg.ultimaGestion.observaciones && (
                                        <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.2rem', fontStyle: 'italic' }}>
                                          "{reg.ultimaGestion.observaciones}"
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div style={{ color: '#94a3b8', padding: '0.4rem 0', fontSize: '0.75rem' }}>Sin gestiones previas.</div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Paginación */}
            {(modoAgenda === 'FULL' || universo === 'BACKLOG') && totalRegistrosFiltrados > 0 && (
              <div className="pagination-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Mostrar por página:</span>
                  <select
                    className="filter-select"
                    style={{ width: 'auto', padding: '0.25rem 0.5rem' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(e.target.value);
                      setPaginaActual(1);
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value="TODOS">Todos ({totalRegistrosFiltrados})</option>
                  </select>
                </div>

                {pageSize !== 'TODOS' && totalPaginas > 1 && (
                  <div className="pagination-controls">
                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
                      disabled={paginaActual === 1}
                    >
                      <ChevronLeft size={14} style={{ verticalAlign: 'middle' }} />
                    </button>

                    {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                      let pNum = i + 1;
                      if (totalPaginas > 5 && paginaActual > 3) {
                        pNum = paginaActual - 2 + i;
                        if (pNum > totalPaginas) pNum = totalPaginas - (4 - i);
                      }
                      return (
                        <button
                          key={pNum}
                          type="button"
                          className={`pagination-btn ${paginaActual === pNum ? 'active' : ''}`}
                          onClick={() => setPaginaActual(pNum)}
                        >
                          {pNum}
                        </button>
                      );
                    })}

                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
                      disabled={paginaActual === totalPaginas}
                    >
                      <ChevronRightIcon size={14} style={{ verticalAlign: 'middle' }} />
                    </button>
                  </div>
                )}

                <div>
                  Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                </div>
              </div>
            )}
          </section>

          {/* Vista Móvil: Tarjetas compactas por cliente */}
          <section className="mobile-cards-container">
            {registrosPaginados.map((reg) => {
              const tramoInfo = TRAMOS_CONFIG[reg.tramo] || { label: reg.tramo, className: '' };
              const isExpanded = expandedRows.has(reg.creditoId);

              return (
                <div key={reg.creditoId} className="mobile-client-card">
                  <div className="mobile-card-header">
                    <div>
                      <strong>{reg.clienteNombre}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#0284c7', fontFamily: 'monospace' }}>
                        {reg.folio}
                      </div>
                    </div>
                    <span className={`badge-tramo ${tramoInfo.className}`}>
                      {tramoInfo.label}
                    </span>
                  </div>

                  {esRango && (
                    <div className="mobile-card-row">
                      <span>Fecha Cobro:</span>
                      <strong>{reg.fechaCobro ? formatearFechaDisplay(reg.fechaCobro) : (reg.fechaVencimiento ? formatearFechaDisplay(reg.fechaVencimiento) : '—')}</strong>
                    </div>
                  )}

                  <div className="mobile-card-row">
                    <span>Cobro Objetivo:</span>
                    <strong>{formatoMoneda(reg.cobroObjetivo)}</strong>
                  </div>

                  <div className="mobile-card-row">
                    <span>Pendiente:</span>
                    <strong style={{ color: '#dc2626' }}>{formatoMoneda(reg.pendienteAlCorte)}</strong>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <button
                      type="button"
                      className="btn-reporte-action"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => toggleRowExpand(reg.creditoId)}
                    >
                      {isExpanded ? 'Ocultar Detalle' : 'Ver Detalle'}
                    </button>

                    <button
                      type="button"
                      className="btn-ficha-action"
                      onClick={() => navigate(`/creditos/${reg.creditoId}`)}
                    >
                      Ficha
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{ background: '#f8fafc', padding: '0.65rem', borderRadius: '6px', fontSize: '0.75rem', marginTop: '0.35rem' }}>
                      <div>Teléfono: <strong>{reg.telefono}</strong></div>
                      <div>Modalidad: <strong>{reg.tipoCartera}</strong> {reg.agrupacionNombre ? `(${reg.agrupacionNombre})` : ''}</div>
                      <div>Frecuencia: <strong>{reg.frecuencia}</strong></div>
                      <div>Cuota: <strong>{formatoMoneda(reg.cuotaVenceHoy)}</strong></div>
                      <div>Vencido (cuotas): <strong>{formatoMoneda(reg.vencidoAnterior)}</strong></div>
                      <div>Moratorio: <strong>{formatoMoneda(reg.moratorio)}</strong></div>
                      <div>Vencido exigible: <strong>{formatoMoneda(reg.vencidoExigible)}</strong></div>
                      <div>Saldo contractual: <strong>{formatoMoneda(reg.saldoTotal)}</strong></div>
                      <div>Exigible total: <strong>{formatoMoneda(reg.exigibleTotal)}</strong></div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}
